import React, { useState, useEffect } from "react";
import { fetchWithAuth } from "../../utils/fetchWithAuth";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

const SECTIONS = [
  { key: "overview",          label: "Overview" },
  { key: "platforms",         label: "Platforms" },
  { key: "social_media",      label: "Social Media" },
  { key: "audience",          label: "Audience" },
  { key: "demographics",      label: "Demographics" },
  { key: "technology_devices",label: "Technology & Devices" },
  { key: "trending",          label: "Trending" },
];

const ROLES = [
  { value: "viewer",  label: "Viewer",  desc: "Access only to granted sections" },
  { value: "editor",  label: "Editor",  desc: "Can view all sections, cannot manage users" },
  { value: "admin",   label: "Admin",   desc: "Full access, can manage non-super users" },
];

function allTrue()  { return Object.fromEntries(SECTIONS.map(s => [s.key, true])); }
function allFalse() { return Object.fromEntries(SECTIONS.map(s => [s.key, false])); }
function defaultPerms(role) { return role === "viewer" ? allFalse() : allTrue(); }

export function EditUserForm({ userId, currentUser, isSuperAdmin, theme: t, onSaved, onCancel }) {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetchWithAuth(`${API}/api/admin/users/${userId}`);
        if (!res.ok) throw new Error((await res.json()).error);
        const u = await res.json();
        setForm({
          name:        u.name || "",
          role:        u.role || "viewer",
          status:      u.active ? "active" : "inactive",
          permissions: u.permissions || defaultPerms(u.role),
        });
        setErr("");
      } catch (e) { setErr(e.message); }
      finally { setLoading(false); }
    })();
  }, [userId]);

  function handleRoleChange(role) {
    const perms = defaultPerms(role);
    setForm(f => ({ ...f, role, permissions: perms }));
  }

  function togglePerm(key) {
    setForm(f => ({ ...f, permissions: { ...f.permissions, [key]: !f.permissions[key] } }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(""); setSuccess("");
    if (!form.name.trim()) { setErr("Name is required"); return; }
    setSaving(true);
    try {
      const res = await fetchWithAuth(`${API}/api/admin/users/${userId}`, {
        method: "PUT",
        body: JSON.stringify({ name: form.name.trim(), role: form.role, permissions: form.permissions, status: form.status }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to update user");
      setSuccess("✓ User updated.");
      setTimeout(onSaved, 900);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  const allRoles = isSuperAdmin ? [...ROLES, { value: "super_admin", label: "Super Admin", desc: "Full access, can manage all users" }] : ROLES;
  const showPermissions = form?.role === "viewer" || form?.role === "editor";

  if (loading) return <div style={{ padding: "40px 0", textAlign: "center", color: t.textFaint, fontSize: 13 }}>Loading…</div>;
  if (!form)   return <div style={{ color: "#DC2626", fontSize: 13 }}>⚠️ {err || "User not found"}</div>;

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: t.text, fontFamily: "Georgia, serif" }}>Edit User</h3>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: t.textMuted }}>Email cannot be changed once created.</p>
        </div>
        <button type="button" onClick={onCancel}
          style={{ background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 8, padding: "7px 16px", color: t.textSub, fontSize: 12, cursor: "pointer" }}>
          Cancel
        </button>
      </div>

      {err     && <div style={{ background: "#DC262611", border: "1px solid #DC262633", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#DC2626" }}>⚠️ {err}</div>}
      {success && <div style={{ background: "#22c55e11", border: "1px solid #22c55e33", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#22c55e" }}>{success}</div>}

      <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16, padding: 24, marginBottom: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
          <div>
            <label style={{ display: "block", fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6, fontWeight: 700 }}>Full Name</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              style={{ width: "100%", background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 8, padding: "9px 12px", color: t.text, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6, fontWeight: 700 }}>Status</label>
            <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
              style={{ width: "100%", background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 8, padding: "9px 12px", color: t.text, fontSize: 13, cursor: "pointer" }}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: showPermissions ? 24 : 0 }}>
          <label style={{ display: "block", fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10, fontWeight: 700 }}>Role</label>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${allRoles.length}, 1fr)`, gap: 10 }}>
            {allRoles.map(r => (
              <button type="button" key={r.value} onClick={() => handleRoleChange(r.value)}
                style={{
                  background: form.role === r.value ? "#2563EB18" : t.surface2,
                  border: `1px solid ${form.role === r.value ? "#2563EB66" : t.border}`,
                  borderRadius: 10, padding: "10px 12px", cursor: "pointer", textAlign: "left",
                }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: form.role === r.value ? "#60a5fa" : t.text, marginBottom: 3 }}>{r.label}</div>
                <div style={{ fontSize: 10, color: t.textFaint, lineHeight: 1.4 }}>{r.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {showPermissions && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <label style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700 }}>Section Access</label>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" onClick={() => setForm(f => ({ ...f, permissions: allTrue() }))}
                  style={{ background: "none", border: "none", color: "#2563EB", fontSize: 11, cursor: "pointer", padding: 0 }}>Grant All</button>
                <button type="button" onClick={() => setForm(f => ({ ...f, permissions: allFalse() }))}
                  style={{ background: "none", border: "none", color: t.textMuted, fontSize: 11, cursor: "pointer", padding: 0 }}>Revoke All</button>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8 }}>
              {SECTIONS.map(s => (
                <label key={s.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: t.surface2, border: `1px solid ${form.permissions[s.key] ? "#2563EB44" : t.border}`, borderRadius: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!form.permissions[s.key]} onChange={() => togglePerm(s.key)}
                    style={{ width: 14, height: 14, accentColor: "#2563EB", cursor: "pointer" }} />
                  <span style={{ fontSize: 12, color: form.permissions[s.key] ? t.text : t.textMuted, fontWeight: form.permissions[s.key] ? 700 : 400 }}>{s.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <button type="submit" disabled={saving}
          style={{ background: saving ? t.surface3 : "#2563EB", border: "none", borderRadius: 10, padding: "11px 24px", color: "#fff", fontSize: 13, fontWeight: 800, cursor: saving ? "default" : "pointer", fontFamily: "Georgia, serif" }}>
          {saving ? "Saving…" : "Save Changes"}
        </button>
        <button type="button" onClick={onCancel}
          style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 10, padding: "11px 24px", color: t.textSub, fontSize: 13, cursor: "pointer", fontFamily: "Georgia, serif" }}>
          Cancel
        </button>
      </div>
    </form>
  );
}
